import {
  TpvCompanyDetail,
  TpvDetailAddress,
  TpvDetailBank,
  TpvDetailContact,
  TpvUnitDetail,
} from '@app/domain/models/tpv/entity-detail.model';
import {
  CompanyDetailDto,
  CompanyDetailResponseDto,
  DetailAddressDto,
  DetailBankDto,
  DetailContactDto,
  UnitDetailDto,
  UnitDetailResponseDto,
} from '@app/data/dtos/tpv/entity-detail.dto';

/** Toma el primer elemento si el bloque viene como array; si es objeto lo devuelve. */
function first<T>(value: T | T[] | undefined | null): T | undefined {
  if (value == null) return undefined;
  return Array.isArray(value) ? value[0] : value;
}

function mapAddress(dto?: DetailAddressDto | DetailAddressDto[]): TpvDetailAddress | undefined {
  const a = first(dto);
  if (!a) return undefined;
  return {
    country: a.country,
    state: a.state,
    city: a.city,
    town: a.town,
    neighborhood: a.neighborhood,
    cp: a.cp,
    street: a.street,
    extNumber: a.ext_number,
    intNumber: a.int_number,
    description: a.description,
  };
}

function mapContact(dto?: DetailContactDto | DetailContactDto[]): TpvDetailContact | undefined {
  const c = first(dto);
  if (!c) return undefined;
  return {
    name: c.name,
    rol: c.rol,
    email: c.email,
    telephonePrincipal: c.telephone_principal,
    telephoneSecond: c.telephone_second,
    phoneMobile: c.phone_mobile,
  };
}

function mapBank(dto?: DetailBankDto | DetailBankDto[]): TpvDetailBank | undefined {
  const b = first(dto);
  if (!b) return undefined;
  return {
    bank: b.bank,
    card: b.card,
    numAccount: b.num_account,
    clabe: b.clabe,
  };
}

/**
 * El backend envuelve el detalle bajo `extra_data.detail` y expone el `id_company`
 * ENTERO (org) en el nivel envelope. Toleramos también la forma plana (sin `detail`).
 */
export function mapCompanyDetail(resp: CompanyDetailResponseDto | CompanyDetailDto): TpvCompanyDetail {
  const env = resp as CompanyDetailResponseDto;
  const d: CompanyDetailDto = env.detail ?? (resp as CompanyDetailDto);
  const orgId = typeof env.id_company === 'number' ? env.id_company : d.org_id_company;
  return {
    // Dentro de `detail`, id_company es el UUID; en el envelope es el entero (org).
    idCompany: typeof d.id_company === 'string' ? d.id_company : '',
    orgIdCompany: orgId,
    name: d.name,
    socialName: d.social_name,
    entity: d.entity,
    rfc: d.rfc,
    represent: d.represent,
    url: d.url ?? undefined,
    status: d.status,
    totalUnits: d.total_units,
    address: mapAddress(d.address),
    contact: mapContact(d.contactPerson ?? d.contact_person),
    bank: mapBank(d.bank),
  };
}

export function mapUnitDetail(resp: UnitDetailResponseDto | UnitDetailDto): TpvUnitDetail {
  const env = resp as UnitDetailResponseDto;
  const d: UnitDetailDto = env.detail ?? (resp as UnitDetailDto);
  const orgId = typeof env.id_company === 'number' ? env.id_company : d.org_id_company;
  return {
    idUnit: d.id_unit ?? env.id_unit ?? '',
    // En `detail`, id_company es el UUID de la empresa.
    idCompany: typeof d.id_company === 'string' ? d.id_company : undefined,
    name: d.name,
    companyName: d.company_name,
    socialName: d.social_name,
    rfc: d.rfc,
    represent: d.represent,
    url: d.url ?? undefined,
    entity: d.entity,
    status: d.status,
    totalMerchants: d.total_merchants,
    orgIdCompany: orgId,
    address: mapAddress(d.address),
    contact: mapContact(d.contactPerson ?? d.contact_person),
    bank: mapBank(d.bank),
  };
}
